import { useId, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { BsArrowRepeat, BsCloudArrowUp, BsPaperclip, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import { useErrorStore } from '#lib/stores/errorStore';

const MAX_BYTES = 2 * 1024 * 1024;

// Client-side pre-check mirrors the backend allowlist (2 MB, document/image types) so obvious
// rejects fail instantly without a round-trip. The backend remains the source of truth.
const ACCEPTED_TYPES = new Set([
	'application/pdf',
	'image/jpeg',
	'image/png',
	'image/webp',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

const ACCEPT_ATTR = [...ACCEPTED_TYPES].join(',');

/**
 * Validate a picked file against type + size. Returns an error code string or null.
 * @param {File} file
 * @returns {string|null}
 */
function validateFile(file) {
	if (!ACCEPTED_TYPES.has(file.type)) return 'EMAIL_TEMPLATE_ATTACHMENT_INVALID_TYPE';
	if (file.size > MAX_BYTES) return 'EMAIL_TEMPLATE_ATTACHMENT_TOO_LARGE';
	return null;
}

/**
 * Human-readable file size.
 * @param {number} bytes
 * @returns {string}
 */
function formatSize(bytes) {
	if (!bytes) return '';
	const mb = bytes / (1024 * 1024);
	return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Drag-and-drop / click attachment field for email templates. Deferred: the picked file is NOT
 * uploaded here — it is held by the editor and uploaded by the page after the template is saved
 * (a new template has no id to upload against yet, so create and edit share one save flow).
 * Sibling to PdfUploadField, minus the upload call.
 */
export default function AttachmentUploadField({ attachment, onSelect, onRemove, disabled = false }) {
	const showError = useErrorStore((s) => s.showError);
	const inputRef = useRef(null);
	const inputId = useId();
	const [dragOver, setDragOver] = useState(false);

	const handleFile = (file) => {
		if (!file || disabled) return;
		const problem = validateFile(file);
		if (problem) {
			showError({ error: { code: problem }, title: 'Anhang konnte nicht hinzugefügt werden' });
			return;
		}
		onSelect(file);
	};

	const onInputChange = (e) => {
		const file = e.target.files?.[0];
		handleFile(file);
		// Reset so picking the same file again re-triggers change.
		e.target.value = '';
	};

	const onDrop = (e) => {
		e.preventDefault();
		setDragOver(false);
		if (disabled) return;
		handleFile(e.dataTransfer.files?.[0]);
	};

	const openPicker = () => {
		if (!disabled) inputRef.current?.click();
	};

	return (
		<div>
			<input
				ref={inputRef}
				id={inputId}
				type="file"
				accept={ACCEPT_ATTR}
				className="sr-only"
				onChange={onInputChange}
				disabled={disabled}
			/>

			{attachment ? (
				<div className="flex items-center gap-3 rounded-card border border-bgBase-500 bg-bgBase-300 px-3 py-2.5">
					<BsPaperclip className="text-lg text-primary-500 flex-shrink-0" />
					<div className="min-w-0 flex-1">
						<div className="text-sm font-medium text-text truncate">{attachment.fileName}</div>
						<div className="text-xs text-bgBase-800">
							{formatSize(attachment.sizeBytes)}
							{attachment.isNew ? ' · wird beim Speichern hochgeladen' : ''}
						</div>
					</div>
					<div className="flex gap-2 flex-shrink-0">
						<Button onClick={openPicker} disabled={disabled} variant="outline" size="sm">
							<BsArrowRepeat /> Ersetzen
						</Button>
						<Button onClick={onRemove} disabled={disabled} variant="outline" size="sm">
							<BsTrash /> Entfernen
						</Button>
					</div>
				</div>
			) : (
				<button
					type="button"
					onClick={openPicker}
					onDragOver={(e) => {
						e.preventDefault();
						if (!disabled) setDragOver(true);
					}}
					onDragLeave={() => setDragOver(false)}
					onDrop={onDrop}
					disabled={disabled}
					className={`w-full flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-card border-2 border-dashed transition-colors ${
						dragOver
							? 'border-primary-400 bg-primary-100'
							: 'border-bgBase-600 bg-bgBase-300 hover:border-bgBase-600'
					} disabled:opacity-60`}
				>
					<BsCloudArrowUp className="text-lg text-bgBase-800" />
					<span className="text-sm text-text">Datei hierher ziehen oder klicken</span>
					<span className="text-xs text-bgBase-800">PDF, Bild oder Office-Datei · max. 2 MB</span>
				</button>
			)}
		</div>
	);
}

AttachmentUploadField.propTypes = {
	attachment: PropTypes.shape({
		fileName: PropTypes.string,
		sizeBytes: PropTypes.number,
		isNew: PropTypes.bool,
	}),
	onSelect: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};
